import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import {
  ModeEditOutlineTwoTone,
  PrintOutlined,
  ArrowDropDown,
  InfoOutlined,
  Add,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import FormDrawer from "components/Shared/FormDrawer";
import ShipmentPricingForm from "./ShipmentPricingForm";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth } from "hooks";
import {
  useAddShipmentPriceMutation,
  useLazyListShipmentPricesQuery,
  useUpdateShipmentPriceMutation,
  useGenerateShipmentEBillMutation,
  usePrintShipmentPriceInvoiceMutation,
  usePrintShipmentPriceCreditNoteMutation,
} from "store/services/shipmentApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import RecordCard from "./RecordCard";

// Credit notes are printed only from the shipment-level Print e-Bill menu.
export const PrintMenuCompact = () => null;

const FieldRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.25 }}>
    <Box
      sx={{
        width: "40%",
        color: "text.primary",
        fontSize: "0.7rem",
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
    <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 500 }}>
      {value ?? "N/A"}
    </Box>
  </Box>
);

const PriceRecordCard = ({
  row,
  onEdit,
  isAccountAdmin,
  isAdmin,
  isEntityAdmin,
  isSystemAdmin,
  isActive,
  status,
  navigate,
  shipmentDetailId,
}) => {
  const statusValue =
    row?.chargeType === "Charge" || row?.chargeType === "Cash on Delivery"
      ? row?.paymentStatus
      : row?.discountStatus;
  const isPositiveStatus =
    statusValue === "Paid" ||
    statusValue === "Applied" ||
    statusValue === "Collected";
  const canEditRow =
    status !== "Delivered" &&
    (isAccountAdmin || isAdmin || isEntityAdmin || isSystemAdmin) &&
    !(
      row.paymentStatus === "Paid" ||
      row.discountStatus === "Applied" ||
      row.paymentStatus === "Collected" ||
      row.paymentStatus === "Refunded" ||
      row?.feeItem?.name === "COD Adjustment Charge" ||
      isActive === false
    );

  return (
    <RecordCard>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
          {row?.feeItem?.name || "N/A"}
        </Typography>
        <Box>
          {canEditRow && (
            <IconButton size="small" onClick={() => onEdit(row)}>
              <ModeEditOutlineTwoTone sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
          <PrintMenuCompact row={row} />
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/app/crm/shipment/price/${row._id}/detail`, {
                state: { shipmentId: shipmentDetailId },
              })
            }
          >
            <InfoOutlined sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <FieldRow label="Type" value={row?.chargeType} />
      <FieldRow
        label="Amount"
        value={
          row?.charge !== undefined
            ? `${row?.charge < 0 || row?.chargeType === "Discount" || row?.feeItem?.name === "COD Adjustment Charge" ? "- " : ""}Rs. ${Math.abs(row.charge).toFixed(2)}`
            : "N/A"
        }
      />
      <FieldRow label="Payer" value={row?.payer} />
      <FieldRow label="Paid At" value={row?.paymentLocation} />
      <Box sx={{ display: "flex", py: 0.25, alignItems: "center" }}>
        <Box
          sx={{
            width: "40%",
            color: "text.primary",
            fontSize: "0.7rem",
            fontWeight: 600,
          }}
        >
          Status
        </Box>
        <Chip
          label={statusValue || "N/A"}
          size="small"
          sx={{
            backgroundColor: isPositiveStatus ? "#e6f4ea" : "#ffeaea",
            color: isPositiveStatus ? "#2e7d32" : "#c62828",
            height: 18,
            fontSize: "0.62rem",
          }}
        />
      </Box>
      {row.chargeType === "Cash on Delivery" && (
        <Box sx={{ display: "flex", py: 0.25, alignItems: "center" }}>
          <Box
            sx={{
              width: "40%",
              color: "text.primary",
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          >
            COD Remitted
          </Box>
          {row.remitStatus === "Transferred" ? (
            <CheckCircle sx={{ color: "#2e7d32", fontSize: "1rem" }} />
          ) : (
            <Cancel sx={{ color: "#c62828", fontSize: "1rem" }} />
          )}
        </Box>
      )}
    </RecordCard>
  );
};

const ShipmentPricingCompact = () => {
  const navigate = useNavigate();
  const {
    isAdmin,
    isSystemAdmin,
    isAccountAdmin,
    isEntityAdmin,
    isCustomer,
    isVendor,
    isContractor,
    isDriver,
    isVendorStaff,
  } = useAuth();
  const canEdit =
    isCustomer || isVendor || isContractor || isDriver || isVendorStaff;
  const { ShipmentDetail } = useShipmentDetailContext();
  const shipmentId = ShipmentDetail?._id;

  const [showPriceForm, setShowPriceForm] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [printAnchor, setPrintAnchor] = useState(null);
  const [bulkInProgress, setBulkInProgress] = useState(false);

  const [fetchPrices, { data: pricesRes, isLoading, isFetching, error }] =
    useLazyListShipmentPricesQuery();
  const [addPrice, { isLoading: addingPrice }] = useAddShipmentPriceMutation();
  const [updatePrice, { isLoading: updatingPrice }] =
    useUpdateShipmentPriceMutation();
  const [generateEBill] = useGenerateShipmentEBillMutation();
  const [printEBill, { isLoading: printingEBill }] =
    usePrintShipmentPriceInvoiceMutation();
  const [printCreditNote, { isLoading: printingCreditNote }] =
    usePrintShipmentPriceCreditNoteMutation();

  const { data: feeTypeList = [], isLoading: loadingFeeTypes } =
    useListListQuery(
      { type: ENTITY_LIST_CONSTANTS.SHIPMENT_FEES, status: "Active" },
      { skip: !shipmentId }
    );
  const { data: discountTypeList = [], isLoading: loadingDiscountTypes } =
    useListListQuery(
      { type: ENTITY_LIST_CONSTANTS.SHIPMENT_DISCOUNT, status: "Active" },
      { skip: !shipmentId }
    );

  const mappedFeeTypeList = useMemo(
    () =>
      Array.isArray(feeTypeList)
        ? feeTypeList.map((i) => ({ label: i?.name, value: i?._id }))
        : [],
    [feeTypeList]
  );
  const mappedDiscountTypeList = useMemo(
    () =>
      Array.isArray(discountTypeList)
        ? discountTypeList.map((i) => ({ label: i?.name, value: i?._id }))
        : [],
    [discountTypeList]
  );

  const isFormDisabled = !shipmentId || loadingFeeTypes || loadingDiscountTypes;

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Error loading shipment prices");
    }
  }, [error]);

  const refreshPrices = useCallback(() => {
    if (shipmentId) {
      fetchPrices({ shipmentId, page: 1, limit: 1000 }).catch(() => {
        toast.error("Failed to load shipment prices");
      });
    }
  }, [shipmentId, fetchPrices]);

  useEffect(() => {
    refreshPrices();
  }, [refreshPrices]);

  const handleCancelPrice = useCallback(() => {
    setShowPriceForm(false);
    setSelectedPrice(null);
  }, []);

  const handleAddPrice = useCallback(
    async (data) => {
      try {
        const targetShipmentId = shipmentId || ShipmentDetail?._id;
        if (!targetShipmentId) {
          toast.error("Shipment ID is missing");
          return;
        }

        const rawItems = Array.isArray(data?.items) ? data.items : [data];
        const hasCharge = rawItems.some((item) => item.chargeType === "Charge");

        // Build cleaned items — feeItem omitted for COD
        const items = rawItems.map((item) => ({
          ...(item.chargeType !== "Cash on Delivery" && item.feeItem
            ? { feeItem: item.feeItem }
            : {}),
          responsibleUser: item.responsibleUser || null,
          responsibleUserType: item.responsibleUserType,
          charge: item.charge,
          chargeType: item.chargeType,
          payer: item.payer,
          paymentLocation: item.paymentLocation,
          ...(item.chargeType === "Charge" || item.chargeType === "Cash on Delivery"
            ? { paymentStatus: item.paymentStatus }
            : { discountStatus: item.discountStatus }),
          deductFromCOD: item.deductFromCOD || null,
        }));

        // Close drawer first, then show full-screen progress overlay
        handleCancelPrice();
        setBulkInProgress(true);

        // Single request — server handles all items in one session/transaction
        await addPrice({
          shipmentId: targetShipmentId,
          items,
        }).unwrap();

        if (hasCharge) {
          try {
            await generateEBill(targetShipmentId).unwrap();
            toast.success("Price and sales eBill created successfully.");
          } catch (eBillErr) {
            toast.error(
              eBillErr?.data?.message ||
                "Price was saved, but the sales eBill could not be created. Please retry eBill generation."
            );
          }
        }

        toast.success(
          items.length === 1
            ? "Price successfully added!"
            : `${items.length} prices successfully added!`
        );
        refreshPrices();
      } catch (err) {
        console.error("Add price error:", err);
        toast.error(err?.data?.message ?? "Couldn't add price(s)!");
      } finally {
        setBulkInProgress(false);
      }
    },
    [
      shipmentId,
      ShipmentDetail?._id,
      addPrice,
      generateEBill,
      handleCancelPrice,
      refreshPrices,
    ]
  );

  const handleUpdatePrice = useCallback(
    async (data) => {
      try {
        const resolvedShipmentId =
          data.shipmentId || shipmentId || ShipmentDetail?._id;
        if (!resolvedShipmentId) {
          toast.error("Shipment ID is missing");
          return;
        }
        if (!data.priceId) {
          toast.error("Price ID is missing");
          return;
        }
        handleCancelPrice();
        await updatePrice({
          shipmentId: resolvedShipmentId,
          priceId: data.priceId,
          ...(data.chargeType !== "Cash on Delivery" && data.feeItem
            ? { feeItem: data.feeItem }
            : {}),
          responsibleUser: data.responsibleUser || null,
          responsibleUserType: data.responsibleUserType,
          charge: data.charge,
          chargeType: data.chargeType,
          payer: data.payer,
          paymentLocation: data.paymentLocation,
          ...(data.chargeType === "Charge" ||
          data.chargeType === "Cash on Delivery"
            ? { paymentStatus: data.paymentStatus }
            : { discountStatus: data.discountStatus }),
          deductFromCOD: data.deductFromCOD || null,
        }).unwrap();
        toast.success("Price successfully updated!");
        refreshPrices();
      } catch (err) {
        console.error("Update price error:", err);
        toast.error(err?.data?.message ?? "Couldn't update price!");
      }
    },
    [shipmentId, ShipmentDetail?._id, updatePrice, handleCancelPrice, refreshPrices]
  );

  const handleEditClick = (row) => {
    if (isFormDisabled) {
      toast.error("Cannot edit price: Shipment or fee/discount types not loaded");
      return;
    }
    setSelectedPrice({
      _id: row._id,
      feeItem: row.feeItem?._id || row.feeItem || "",
      responsibleUser:
        row.responsibleUser?._id || row.responsibleUser || null,
      charge: Math.abs(row.charge || 0),
      chargeType: row.chargeType || "Charge",
      payer: row.payer || "Sender",
      paymentLocation:
        row.paymentLocation ||
        (row.payer === "Receiver" || row.chargeType === "Cash on Delivery"
          ? "Destination"
          : "Source"),
      paymentStatus: row.paymentStatus || "Not Paid",
      discountStatus: row.discountStatus || "Not Applied",
      source: row.source || "",
      sourceRef: row.sourceRef || "",
      user: row.user || { name: "" },
    });
    setShowPriceForm(true);
  };

  const records = Array.isArray(pricesRes?.data) ? pricesRes.data : [];

  const eBills = useMemo(() => {
    const unique = new Map();
    records.forEach((price) => {
      if (price?.eBillId && !unique.has(price.eBillId)) {
        unique.set(price.eBillId, price);
      }
    });
    return [...unique.values()];
  }, [records]);

  const creditNotes = useMemo(() => {
    const unique = new Map();
    records.forEach((price) => {
      (price?.creditNotes || []).forEach((creditNote) => {
        if (creditNote?.creditNoteId && !unique.has(creditNote.creditNoteId)) {
          unique.set(creditNote.creditNoteId, {
            ...creditNote,
            priceId: price._id,
            payer: price.payer,
          });
        }
      });
    });
    return [...unique.values()];
  }, [records]);

  const netSummary = useMemo(() => {
    if (!records.length && pricesRes) {
      return {
        totalCharges: pricesRes?.totalCharges ?? 0,
        totalDiscounts: pricesRes?.totalDiscounts ?? 0,
        remainingAmount: pricesRes?.remainingShipmentAmount ?? 0,
      };
    }

    let calculatedCharges = 0;
    let calculatedDiscounts = 0;
    let calculatedPaid = 0;

    records.forEach((row) => {
      const charge = Number(row?.charge || 0);
      const isCodAdjustment =
        row?.feeItem?.name === "COD Adjustment Charge" ||
        row?.source === "ShipmentRefund";

      if (row?.chargeType === "Charge") {
        if (isCodAdjustment) {
          calculatedCharges -= Math.abs(charge);
        } else if (charge < 0) {
          calculatedCharges += charge;
        } else {
          calculatedCharges += charge;
        }

        if (
          (row?.paymentStatus === "Paid" || row?.paymentStatus === "Collected") &&
          !isCodAdjustment &&
          charge > 0
        ) {
          calculatedPaid += charge;
        }
      } else if (
        row?.chargeType === "Discount" &&
        row?.discountStatus === "Applied"
      ) {
        calculatedDiscounts += Math.abs(charge);
      }
    });

    const remaining = Math.max(
      0,
      calculatedCharges - calculatedDiscounts - calculatedPaid
    );

    return {
      totalCharges: calculatedCharges,
      totalDiscounts: calculatedDiscounts,
      remainingAmount: remaining,
    };
  }, [records, pricesRes]);

  const handlePrintEBill = async (priceId) => {
    try {
      const response = await printEBill(priceId).unwrap();
      const printUrl =
        response?.eBillPrintUrl ||
        response?.data?.print_url ||
        response?.data?.printUrl ||
        response?.data?.url;
      if (!printUrl) throw new Error("No eBill download URL returned");
      window.open(printUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(
        error?.data?.message || error.message || "eBill download failed"
      );
    } finally {
      setPrintAnchor(null);
    }
  };

  const handlePrintCreditNote = async (creditNote) => {
    try {
      const response = await printCreditNote({
        priceId: creditNote.priceId,
        creditNoteId: creditNote.creditNoteId,
      }).unwrap();
      const printUrl =
        response?.creditNotePrintUrl ||
        response?.data?.print_url ||
        response?.data?.printUrl ||
        response?.data?.url;
      if (!printUrl) throw new Error("No credit note print URL returned");
      window.open(printUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(
        err?.data?.message || err.message || "Credit note print failed"
      );
    } finally {
      setPrintAnchor(null);
    }
  };

  // Extract credit limit and credit amount
  const creditLimit = ShipmentDetail?.vendor?.vendorLimits?.creditLimit;
  const creditAmount = ShipmentDetail?.balanceDetails?.totalUnpaidCharges;

  useEffect(() => {
    if (ShipmentDetail?.vendor && creditAmount >= creditLimit && !isVendor) {
      toast.warning(
        `⚠ Credit limit exceeded: Used: Rs. ${creditAmount} / Limit: ${creditLimit}`,
        { autoClose: 5000, style: { background: "#fff3cd", color: "#856404" } }
      );
    }
  }, [creditLimit, creditAmount, ShipmentDetail?.vendor, isVendor]);

  return (
    <Box sx={{ p: 1 }}>
      {/* Bulk pricing progress overlay */}
      {bulkInProgress && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            gap: 2,
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: "#fff" }} />
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 600,
              letterSpacing: 0.5,
              textAlign: "center",
              px: 2,
            }}
          >
            Shipment Pricing in progress…
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            Please wait while all prices are being saved
          </Typography>
        </Box>
      )}

      {/* Display credit information if vendor exists */}
      {ShipmentDetail?.vendor?.vendorLimits?.creditLimit != null && !isVendor && (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            mb: 1,
            borderRadius: 1.5,
            border: "1px solid",
            backgroundColor: (theme) =>
              creditAmount >= creditLimit
                ? theme.palette.mode === "dark"
                  ? "#2a0000"
                  : "#fff5f5"
                : theme.palette.mode === "dark"
                  ? "#002a1a"
                  : "#f0fdf4",
            borderColor: (theme) =>
              creditAmount >= creditLimit
                ? theme.palette.error.main
                : theme.palette.success.main,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.68rem",
              color: (theme) =>
                creditAmount >= creditLimit
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              fontWeight: 500,
            }}
          >
            Credit Limit: Rs. {creditLimit?.toLocaleString() || "0"} | Used: Rs.{" "}
            {creditAmount?.toLocaleString() || "0"}
          </Typography>
        </Box>
      )}

      {!canEdit && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.75,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          {ShipmentDetail?.status !== "Delivered" && (
            <Button
              size="small"
              startIcon={<Add sx={{ fontSize: "0.9rem" }} />}
              variant="outlined"
              disabled={
                ShipmentDetail?.isActive === false ||
                isFormDisabled ||
                showPriceForm
              }
              onClick={() => {
                if (isFormDisabled) {
                  toast.error(
                    "Cannot add price: Shipment or fee/discount types not loaded"
                  );
                  return;
                }
                setSelectedPrice(null);
                setShowPriceForm(true);
              }}
              sx={{ fontSize: "0.68rem", py: 0.25, textTransform: "none" }}
            >
              Add Pricing
            </Button>
          )}
          {isAccountAdmin && (eBills.length > 0 || creditNotes.length > 0) && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintOutlined sx={{ fontSize: "0.9rem" }} />}
                endIcon={<ArrowDropDown sx={{ fontSize: "0.9rem" }} />}
                onClick={(event) => setPrintAnchor(event.currentTarget)}
                disabled={printingEBill || printingCreditNote}
                sx={{ fontSize: "0.68rem", py: 0.25, textTransform: "none" }}
              >
                Print e-Bill / Credit Note ({eBills.length + creditNotes.length})
              </Button>
              <Menu
                anchorEl={printAnchor}
                open={Boolean(printAnchor)}
                onClose={() => setPrintAnchor(null)}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    p: 0.5,
                    minWidth: 220,
                    borderRadius: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
                  },
                }}
              >
                {eBills.map((bill, index) => (
                  <MenuItem
                    key={bill.eBillId}
                    onClick={() => handlePrintEBill(bill._id)}
                    sx={{ px: 1.5, py: 0.75, borderRadius: 1 }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.78rem",
                        fontWeight: 500,
                      }}
                      secondaryTypographyProps={{ fontSize: "0.68rem" }}
                      primary={`Print ${bill.payer || "Shipment"} e-Bill`}
                      secondary={bill.eBillInvoiceNo || `e-Bill ${index + 1}`}
                    />
                  </MenuItem>
                ))}
                {creditNotes.map((creditNote, index) => (
                  <MenuItem
                    key={creditNote.creditNoteId}
                    onClick={() => handlePrintCreditNote(creditNote)}
                    sx={{ px: 1.5, py: 0.75, borderRadius: 1 }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.78rem",
                        fontWeight: 500,
                      }}
                      secondaryTypographyProps={{ fontSize: "0.68rem" }}
                      primary={`Print ${creditNote.payer || "Shipment"} credit note`}
                      secondary={
                        creditNote.creditNoteCode || creditNote.creditNoteId
                      }
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      )}
      {isLoading || isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={18} />
        </Box>
      ) : records.length === 0 ? (
        <Typography
          sx={{
            fontSize: "0.74rem",
            color: "text.secondary",
            textAlign: "center",
            py: 2,
          }}
        >
          No shipment prices found.
        </Typography>
      ) : (
        records.map((row) => (
          <PriceRecordCard
            key={row._id}
            row={row}
            onEdit={handleEditClick}
            isAccountAdmin={isAccountAdmin}
            isAdmin={isAdmin}
            isEntityAdmin={isEntityAdmin}
            isSystemAdmin={isSystemAdmin}
            isActive={ShipmentDetail?.isActive}
            status={ShipmentDetail?.status}
            navigate={navigate}
            shipmentDetailId={ShipmentDetail?._id}
          />
        ))
      )}
      {pricesRes && (
        <RecordCard highlight sx={{ mt: 1 }}>
          <FieldRow
            label="Total Charges"
            value={`Rs. ${(netSummary?.totalCharges ?? pricesRes?.totalCharges ?? 0).toFixed(2)}`}
          />
          <FieldRow
            label="Total Discounts"
            value={`- Rs. ${(netSummary?.totalDiscounts ?? pricesRes?.totalDiscounts ?? 0).toFixed(2)}`}
          />
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: "flex", py: 0.25 }}>
            <Box sx={{ width: "40%", fontSize: "0.74rem", fontWeight: 700 }}>
              Remaining
            </Box>
            <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 700 }}>
              Rs. {(netSummary?.remainingAmount ?? pricesRes?.remainingShipmentAmount ?? 0).toFixed(2)}
            </Box>
          </Box>
        </RecordCard>
      )}
      <FormDrawer
        title={selectedPrice ? "Edit Price" : "Add Price"}
        open={showPriceForm}
        onClose={handleCancelPrice}
        width={850}
      >
        <ShipmentPricingForm
          isBusy={addingPrice || updatingPrice || bulkInProgress}
          price={selectedPrice}
          shipmentId={shipmentId}
          onCancel={handleCancelPrice}
          onSubmit={selectedPrice ? handleUpdatePrice : handleAddPrice}
          feeTypeList={mappedFeeTypeList}
          discountTypeList={mappedDiscountTypeList}
        />
      </FormDrawer>
    </Box>
  );
};

export default ShipmentPricingCompact;
